import { useGetAllNews } from "@/api/cms";
import { newsColumnsGridAttrs } from "@/lib/cms-puck/blocks/column-count";
import { normalizeGalleryColumns } from "@/lib/cms-puck/blocks/gallery";
import { type SupportedLocale } from "@/i18n";
import type { NewsItem } from "@/lib/cms/cms-types";
import { resolveNewsForBlock } from "@/lib/cms-puck/blocks/news-display";
import { useTranslation } from "react-i18next";
import { cmsNew } from "@/lib/cms/cms-new-i18n";
import {
  resolveCompositeInnerLayout,
  type LayoutGap,
} from "@/lib/cms-puck/layout";

/**
 * Aperçu éditeur du bloc `News` : récupère les actualités du tenant via le hook
 * existant (`useGetAllNews`, scoping serveur) et affiche une représentation
 * façon front — une éventuelle « à la une » (pleine largeur) puis une grille des
 * suivantes (image + titre + date). Pas de sélection manuelle : le bloc affiche
 * les actualités publiées du tenant ; les props (columns/limit/featured) sont
 * STRUCTURELLES. Fonctionnel, pas forcément fidèle au pixel près au front public
 * (le rendu réel y est fait à partir de `refs.news` résolu côté API). Si aucune
 * actualité, on affiche une note explicative.
 */
export function NewsPreview({
  mode,
  newsIds,
  columns,
  limit,
  featured,
  gap,
  gapCustomPx,
}: {
  mode?: "auto" | "manual";
  newsIds?: string[];
  columns: number;
  limit: number;
  featured: boolean;
  gap?: LayoutGap | null;
  gapCustomPx?: number | null;
}) {
  const { data: allNews = [], isLoading } = useGetAllNews();
  const { i18n } = useTranslation();
  const lang = i18n.language;
  const news = resolveNewsForBlock(
    allNews.filter((n): n is NewsItem & { id: string } => !!n.id),
    { mode, newsIds, limit, featured },
  );

  if (isLoading) {
    return (
      <div className="w-full">
        <p className="text-sm text-slate-400">
          {cmsNew("cms.new.preview.news.loading", "Chargement des actualités…")}
        </p>
      </div>
    );
  }

  if (news.length === 0) {
    return (
      <div className="w-full">
        <div className="rounded border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
          {cmsNew(
            "cms.new.preview.news.empty",
            "Actualités publiées — aucune actualité pour le moment.",
          )}
        </div>
      </div>
    );
  }

  const featuredItem = featured ? news[0] : undefined;
  const gridItems = featured ? news.slice(1) : news;
  const grid = newsColumnsGridAttrs(normalizeGalleryColumns(columns));
  const inner = resolveCompositeInnerLayout({
    gap,
    gapCustomPx,
    withAlignItems: false,
  });

  const NewsThumb = ({ item }: { item: (typeof news)[number] }) => {
    const att = item.attachments?.[0];
    const url = att?.mediumPublicUrl ?? att?.publicUrl ?? att?.largePublicUrl;
    const title = item.title?.[lang as SupportedLocale] ?? item.title?.fr ?? "";
    return (
      <div className="h-fit">
        <div className="flex aspect-video w-full items-center justify-center overflow-hidden rounded bg-slate-100">
          {url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={url} alt={title} className="h-full w-full object-cover" />
          ) : (
            <span className="px-2 text-center text-[11px] text-slate-400">
              {title || cmsNew("cms.new.preview.news.noImage", "Sans image")}
            </span>
          )}
        </div>
        <p className="pt-1 text-xs text-slate-400">
          {item.date ? new Date(item.date).toLocaleDateString(lang) : ""}
        </p>
        <p
          className="line-clamp-2 text-sm font-medium uppercase text-slate-700"
          style={{ textTransform: "uppercase" }}
        >
          {title || cmsNew("cms.new.news.noTitle", "(Sans titre)")}
        </p>
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="flex flex-col">
        {featuredItem && (
          <div className="mb-16">
            <NewsThumb item={featuredItem} />
          </div>
        )}
        {gridItems.length > 0 && (
          <div
            className={`${grid.className} ${inner.className}`}
            style={{ ...grid.style, ...inner.style }}
          >
            {gridItems.map((item) => (
              <NewsThumb key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
      <p className="mt-3 text-xs text-slate-400">
        {cmsNew(
          "cms.new.preview.news.caption",
          "Aperçu des actualités publiées (rendu réel sur le site public).",
        )}
      </p>
    </div>
  );
}
